/**
 * Platform HQ — Reset Health panel (last N days).
 * Shows password-reset requests, failed attempts and live sign-in lockouts
 * per customer email so operators can spot locked-out admins early.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Activity, Loader2, RefreshCw, Search, ShieldAlert } from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;

const REASON_LABELS = {
  invalid_token: 'Invalid link',
  token_used: 'Link already used',
  token_expired: 'Link expired',
  weak_password: 'Weak password rejected',
  account_inactive: 'Account archived',
  rate_limited: 'Too many requests',
  unknown_email: 'Email not registered',
};

export const ResetHealthPanel = ({ headers, onFind }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/platform/reset-health`, { headers, params: { days: 7 } });
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  const fmtWhen = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return isNaN(d) ? '—' : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  return (
    <Card className="bg-slate-900 border-slate-800" data-testid="reset-health-panel">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 mb-1">
          <h2 className="font-semibold text-slate-100 flex items-center gap-2">
            <Activity className="w-4 h-4 text-emerald-400" />Reset health — last {data?.days || 7} days
          </h2>
          <Button
            size="sm" variant="ghost" onClick={load} disabled={loading}
            className="text-slate-300 hover:text-white" data-testid="reset-health-refresh"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
        <p className="text-xs text-slate-400 mb-4">
          Customers who requested or failed a password reset, plus live sign-in lockouts (5 wrong passwords → 15-minute block).
          Spot locked-out admins before they email you.
        </p>

        {error && <p className="text-sm text-red-400" data-testid="reset-health-error">{error}</p>}

        {!error && !loading && rows.length === 0 && (
          <p className="text-sm text-slate-500" data-testid="reset-health-empty">
            No reset activity in the last {data?.days || 7} days. All quiet. 🎉
          </p>
        )}

        {rows.length > 0 && (
          <div className="overflow-x-auto" data-testid="reset-health-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-800">
                  <th className="py-2 pr-3">Email</th>
                  <th className="py-2 pr-3">Company</th>
                  <th className="py-2 pr-3 text-center">Requests</th>
                  <th className="py-2 pr-3 text-center">Completed</th>
                  <th className="py-2 pr-3 text-center">Failures</th>
                  <th className="py-2 pr-3">Last issue</th>
                  <th className="py-2 pr-3">Last activity</th>
                  <th className="py-2 pr-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.email} className="border-b border-slate-800/60" data-testid={`reset-health-row-${r.email}`}>
                    <td className="py-2 pr-3 font-mono text-xs text-slate-200 break-all">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {r.email}
                        {r.sign_in_locked && (
                          <Badge className="bg-red-900 text-red-200 border border-red-700 text-[10px]" data-testid={`reset-health-locked-${r.email}`}>
                            <ShieldAlert className="w-3 h-3 mr-0.5" />Sign-in locked
                          </Badge>
                        )}
                      </div>
                      {r.user_name && <span className="text-slate-500">{r.user_name}</span>}
                    </td>
                    <td className="py-2 pr-3 text-slate-300 text-xs">
                      {(r.companies || []).length === 0 ? '—' : r.companies.map((c) => c.name).join(', ')}
                    </td>
                    <td className="py-2 pr-3 text-center text-slate-300">{r.requests}</td>
                    <td className="py-2 pr-3 text-center">
                      {r.completed > 0
                        ? <span className="text-emerald-400">{r.completed}</span>
                        : <span className="text-slate-500">0</span>}
                    </td>
                    <td className="py-2 pr-3 text-center">
                      {r.failures > 0
                        ? <span className="text-red-400 font-semibold">{r.failures}</span>
                        : <span className="text-slate-500">0</span>}
                    </td>
                    <td className="py-2 pr-3 text-xs text-slate-400">
                      {r.last_failure_reason ? (REASON_LABELS[r.last_failure_reason] || r.last_failure_reason) : '—'}
                    </td>
                    <td className="py-2 pr-3 text-xs text-slate-400 whitespace-nowrap">{fmtWhen(r.last_activity_at)}</td>
                    <td className="py-2 pr-3 text-right">
                      <Button
                        size="sm" variant="outline"
                        onClick={() => onFind?.(r.email)}
                        className="border-slate-700 text-slate-300 hover:bg-slate-800"
                        data-testid={`reset-health-find-${r.email}`}
                      >
                        <Search className="w-3.5 h-3.5 mr-1.5" />Find
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data?.unattributed_failures > 0 && (
              <p className="text-xs text-slate-500 mt-3" data-testid="reset-health-unattributed">
                Plus {data.unattributed_failures} failed attempt{data.unattributed_failures !== 1 ? 's' : ''} with unrecognizable links (no email attribution).
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ResetHealthPanel;
